/**
 * Cancelling and editing a sale that has already been recorded.
 *
 * Both are open to anybody allowed to sell, with no approval — the owner's
 * decision — and both are limited in time instead:
 *
 *   CANCEL   for 14 days after it: sold on the 1st, it can be cancelled
 *            until the end of the 15th.
 *   EDIT     for 30 days after it: sold on the 1st, it can be edited until
 *            the end of the 31st.
 *
 * The owner widened both on 2026-10-18, from the testers: the day of the sale
 * and seven days were too short for a return or a mistake noticed late.
 *
 * "The day of the sale" is the pharmacy's own calendar day, never the
 * server's: at 1am in Mogadishu the server still says yesterday.
 *
 * Nothing is wiped by either. A cancelled sale stays in the history marked
 * Cancelled; an edited one shows what changed, who changed it, and why — the
 * record is what catches a sale rung up, paid, and quietly edited down later.
 */

export const SALE_CHANGE_REASONS = [
  "returned",
  "wrong_medicine",
  "wrong_quantity",
  "wrong_price",
  "other",
] as const;

export type SaleChangeReason = (typeof SALE_CHANGE_REASONS)[number];

/**
 * How many medicines the Add sale page offers as "Most sold" before anything
 * is typed. Five, as the owner asked: more makes a long list on a small phone.
 */
export const MOST_SOLD_COUNT = 5;

/** How many days after the sale day a cancel is still allowed. */
export const CANCEL_DAYS = 14;

/** How many days after the sale day an edit is still allowed. */
export const EDIT_DAYS = 30;

/** "Other" with nothing written says nothing at all. */
export function changeNoteRequired(reason: SaleChangeReason, note?: string | null): boolean {
  return reason === "other" && !(note ?? "").trim();
}

/** Both dates YYYY-MM-DD, in the pharmacy's calendar. */
export function canCancelSale(soldOn: string, today: string): boolean {
  return soldOn <= today && soldOn >= shiftDays(today, -CANCEL_DAYS);
}

export function canEditSale(soldOn: string, today: string): boolean {
  return soldOn <= today && soldOn >= shiftDays(today, -EDIT_DAYS);
}

/** A YYYY-MM-DD date moved by whole days. Plain calendar arithmetic, in UTC. */
function shiftDays(isoDate: string, days: number): string {
  const at = new Date(`${isoDate}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}
